import { useState } from 'react';
import { Plus, Edit2, Trash2, TrendingUp, TrendingDown, Calendar, Percent, Hash } from 'lucide-react';
import { format } from 'date-fns';
import {
  ForecastAssumption,
  FactorType,
  CreateAssumptionDto,
  UpdateAssumptionDto,
} from '../../services/forecasts.service';

interface ForecastAssumptionsPanelProps {
  forecastId: string;
  assumptions: ForecastAssumption[];
  isLoading: boolean;
  onCreateAssumption: (dto: CreateAssumptionDto) => void;
  onUpdateAssumption: (assumptionId: string, dto: UpdateAssumptionDto) => void;
  onDeleteAssumption: (assumptionId: string) => void;
}

const FACTOR_TYPE_LABELS: Record<FactorType, string> = {
  BASE_VOLUME: 'Base Volume',
  GROWTH: 'Growth',
  EFFICIENCY: 'Efficiency',
  EVENT_OUTAGE: 'Outage Event',
  EVENT_PROMOTION: 'Promotion',
  EVENT_LAUNCH: 'Product Launch',
  EVENT_OTHER: 'Other Event',
};

const FACTOR_TYPE_COLORS: Record<FactorType, string> = {
  BASE_VOLUME: 'bg-gray-100 text-gray-800',
  GROWTH: 'bg-green-100 text-green-800',
  EFFICIENCY: 'bg-blue-100 text-blue-800',
  EVENT_OUTAGE: 'bg-red-100 text-red-800',
  EVENT_PROMOTION: 'bg-purple-100 text-purple-800',
  EVENT_LAUNCH: 'bg-yellow-100 text-yellow-800',
  EVENT_OTHER: 'bg-orange-100 text-orange-800',
};

const FACTOR_TYPE_OPTIONS: FactorType[] = [
  'GROWTH',
  'EFFICIENCY',
  'EVENT_PROMOTION',
  'EVENT_LAUNCH',
  'EVENT_OUTAGE',
  'EVENT_OTHER',
];

interface AssumptionFormData {
  factorType: FactorType;
  name: string;
  description: string;
  percentageImpact: string;
  absoluteImpact: string;
  startDate: string;
  endDate: string;
}

const initialFormData: AssumptionFormData = {
  factorType: 'GROWTH',
  name: '',
  description: '',
  percentageImpact: '',
  absoluteImpact: '',
  startDate: '',
  endDate: '',
};

export function ForecastAssumptionsPanel({
  assumptions,
  isLoading,
  onCreateAssumption,
  onUpdateAssumption,
  onDeleteAssumption,
}: ForecastAssumptionsPanelProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<AssumptionFormData>(initialFormData);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const dto: CreateAssumptionDto = {
      factorType: formData.factorType,
      name: formData.name,
      description: formData.description || undefined,
      percentageImpact: formData.percentageImpact ? parseFloat(formData.percentageImpact) : undefined,
      absoluteImpact: formData.absoluteImpact ? parseFloat(formData.absoluteImpact) : undefined,
      startDate: formData.startDate || undefined,
      endDate: formData.endDate || undefined,
    };

    if (editingId) {
      onUpdateAssumption(editingId, dto);
      setEditingId(null);
    } else {
      onCreateAssumption(dto);
    }

    setFormData(initialFormData);
    setShowForm(false);
  };

  const handleEdit = (assumption: ForecastAssumption) => {
    setFormData({
      factorType: assumption.factorType,
      name: assumption.name,
      description: assumption.description || '',
      percentageImpact: assumption.percentageImpact?.toString() || '',
      absoluteImpact: assumption.absoluteImpact?.toString() || '',
      startDate: assumption.startDate ? assumption.startDate.split('T')[0] : '',
      endDate: assumption.endDate ? assumption.endDate.split('T')[0] : '',
    });
    setEditingId(assumption.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setEditingId(null);
    setShowForm(false);
  };

  const handleDelete = (assumptionId: string) => {
    if (confirm('Are you sure you want to delete this assumption?')) {
      onDeleteAssumption(assumptionId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading assumptions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Forecast Assumptions</h3>
          <p className="text-sm text-gray-600">
            Define factors that affect your forecast: growth, efficiency improvements, and events
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Assumption
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4 space-y-4 border border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Factor Type</label>
              <select
                value={formData.factorType}
                onChange={(e) => setFormData({ ...formData, factorType: e.target.value as FactorType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {FACTOR_TYPE_OPTIONS.map((type) => (
                  <option key={type} value={type}>
                    {FACTOR_TYPE_LABELS[type]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Q1 Product Launch"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this assumption"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Percent className="w-4 h-4 inline mr-1" />
                Percentage Impact
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.percentageImpact}
                onChange={(e) => setFormData({ ...formData, percentageImpact: e.target.value })}
                placeholder="e.g., +5 or -10"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Positive = increase, negative = decrease</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Hash className="w-4 h-4 inline mr-1" />
                Absolute Impact
              </label>
              <input
                type="number"
                value={formData.absoluteImpact}
                onChange={(e) => setFormData({ ...formData, absoluteImpact: e.target.value })}
                placeholder="e.g., +500 or -200"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Fixed volume change (contacts)</p>
            </div>
          </div>

          {/* Date range for events */}
          {formData.factorType.startsWith('EVENT_') && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Start Date
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {editingId ? 'Update' : 'Add'} Assumption
            </button>
          </div>
        </form>
      )}

      {/* Assumptions List */}
      {assumptions.length === 0 ? (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p>No assumptions defined yet.</p>
          <p className="text-sm">Add assumptions to model growth, efficiency, and events.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {assumptions.map((assumption) => (
            <div
              key={assumption.id}
              className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
            >
              <div className="flex items-center gap-4">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${FACTOR_TYPE_COLORS[assumption.factorType]}`}>
                  {FACTOR_TYPE_LABELS[assumption.factorType]}
                </span>
                <div>
                  <div className="font-medium text-gray-900">{assumption.name}</div>
                  {assumption.description && (
                    <div className="text-sm text-gray-500">{assumption.description}</div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Impact display */}
                <div className="flex items-center gap-2 text-sm">
                  {assumption.percentageImpact !== null && assumption.percentageImpact !== undefined && (
                    <span className={`flex items-center ${assumption.percentageImpact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {assumption.percentageImpact >= 0 ? (
                        <TrendingUp className="w-4 h-4 mr-1" />
                      ) : (
                        <TrendingDown className="w-4 h-4 mr-1" />
                      )}
                      {assumption.percentageImpact >= 0 ? '+' : ''}{assumption.percentageImpact}%
                    </span>
                  )}
                  {assumption.absoluteImpact !== null && assumption.absoluteImpact !== undefined && (
                    <span className={`flex items-center ${assumption.absoluteImpact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {assumption.absoluteImpact >= 0 ? '+' : ''}{assumption.absoluteImpact.toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Date range for events */}
                {assumption.startDate && (
                  <span className="text-xs text-gray-500">
                    {format(new Date(assumption.startDate), 'MMM d')}
                    {assumption.endDate && ` - ${format(new Date(assumption.endDate), 'MMM d')}`}
                  </span>
                )}

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(assumption)}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(assumption.id)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
